import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslocoService } from '@ngneat/transloco';
import { delay } from 'rxjs';

import { ControlTemplate } from '../_interface/control-template.enum';
import { DynamicIdService } from 'shared/_services/dynamic.id.service';
import { tryParseJson } from 'shared/_functions/tryParseJson';
import { isControlRequired } from '../functions/check-if-control-required.fn';
import { selectUrlToGetFile } from '../../../../journal/src/_selectors/journal.selectors';
import {FormFieldWarnings} from '../../../../journal/src/_interface/form-field-warnings.interface';

interface FileUploadEvent {
	serverId: string;
	file: any;
}

interface FileProgressEvent {
	progress: number;
}

@UntilDestroy()
@Component({
	selector: 'form-file',
	templateUrl: './file.component.html',
	styleUrls: ['./file.component.scss']
})
export class FileComponent implements OnInit, AfterViewInit, OnDestroy {
	private resizeObserver?: ResizeObserver;

	@ViewChild('pondWrapper', { static: false }) private pondWrapper: any;
	@ViewChild('fileContent', { read: ElementRef }) private fileContent!: ElementRef;
	@ViewChild('fileName', { read: ElementRef }) private fileName!: ElementRef;

	@Input() set setWarnings(warnings: FormFieldWarnings) {
		this.warnings = warnings;
	}
	@Input() controlGroup!: UntypedFormGroup;
	@Input() id: string = this.dynamicIdService.getId('file');
	@Input() label?: string;
	@Input() instantUpload = false;
	@Input() extraClass?: string;
	@Input() template: ControlTemplate = ControlTemplate.inline;
	@Input() resetFile = true;

	@Output() valueChange = new EventEmitter<string>();

	protected readonly ControlTemplate = ControlTemplate;
	protected showTooltip = false;
	protected endpoint?: string;
	protected warnings?: FormFieldWarnings;
	protected isControlRequired = false;
	protected fileLoading = false;
	protected fileLoadSuccess = false;
	protected progressValue = 0;

	constructor(
		private dynamicIdService: DynamicIdService,
		private store: Store,
		private translocoService: TranslocoService
	) {}

	private validateControlGroup(): void {
		if (!this.controlGroup) {
			throw new Error('control is required');
		}
	}

	private initializeComponent(): void {
		this.isControlRequired = isControlRequired(this.controlGroup);
	}

	private setupSubscriptions(): void {
		this.setupEndpointSubscription();
		this.setupValueChangesSubscription();
	}

	private setupEndpointSubscription(): void {
		this.store.select(selectUrlToGetFile)
			.pipe(untilDestroyed(this))
			.subscribe(endpoint => this.endpoint = endpoint);
	}

	private setupValueChangesSubscription(): void {
		this.controlGroup.valueChanges
			.pipe(untilDestroyed(this), delay(100))
			.subscribe(() => this.updateTooltipVisibility());
	}

	private setupResizeObserver(): void {
		this.resizeObserver = new ResizeObserver(() => {
			this.updateTooltipVisibility();
		});

		if (this.fileContent?.nativeElement) {
			this.resizeObserver.observe(this.fileContent.nativeElement);
		}
	}

	private updateTooltipVisibility(): void {
		if (!this.fileName?.nativeElement) return;

		const fileNameElement = this.fileName.nativeElement;
		this.showTooltip = fileNameElement.offsetWidth < fileNameElement.scrollWidth;
	}

	private setFileUploadError(): void {
		const currentErrors = this.controlGroup.errors || {};
		this.controlGroup.setErrors({
			...currentErrors,
			errors: this.translocoService.translate('formElements.generic.fileUploadFailed')
		});
	}

	ngOnInit(): void {
		this.validateControlGroup();
		this.initializeComponent();
		this.setupSubscriptions();
	}

	ngAfterViewInit(): void {
		this.setupResizeObserver();
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
	}

	uploaded(event: FileUploadEvent): void {
		const response = tryParseJson(event.serverId);
		this.controlGroup.patchValue(response);
		this.fileLoadSuccess = true;
	}

	markFormGroup = (): void => {
		this.controlGroup.markAsTouched();
		this.controlGroup.markAsDirty();
	}

	browseFile = (event: MouseEvent): void => {
		event.stopPropagation();
		this.pondWrapper.myPond.browse();
	}

	reset(event: MouseEvent): void {
		event.stopPropagation();
		this.pondWrapper.myPond.removeFile();
		this.removeFile();
	}

	removeFile(): void {
		this.controlGroup.reset();
		this.controlGroup.markAsDirty();
		this.progressValue = 0;
		this.fileLoadSuccess = false;
	}

	fileLoadingError(error: boolean): void {
		if (!error) return;
		this.fileLoadSuccess = false;
		this.setFileUploadError();
	}

	processFileProgress(event: FileProgressEvent): void {
		this.progressValue = event.progress * 100;
		if (event.progress === 1) {
			this.progressValue = 0;
		}
	}
}
